import { supabaseService } from '../db/supabase';
import type { AuthSession, PapelUsuario, UsuarioAcesso } from '../types';

function mapSession(row: UsuarioAcesso, userId: string, email: string): AuthSession {
  return {
    userId,
    email,
    nome: row.nome,
    papel: row.papel,
    campusSiglas: row.campus_siglas || [],
    podeVerTodosCampus: row.pode_ver_todos_campus,
  };
}

export class AuthService {
  private supabase = supabaseService.getClient();

  async signIn(emailOrUsername: string, password: string): Promise<AuthSession> {
    const fullEmail = emailOrUsername.includes('@') ? emailOrUsername : `${emailOrUsername}@inventariar.utfpr`;
    const { data, error } = await this.supabase.auth.signInWithPassword({ email: fullEmail, password });

    if (error) throw error;
    if (!data.user || !data.session) throw new Error('Usuário não encontrado.');

    const acesso = await this.fetchAcesso(data.user.id);
    if (!acesso) throw new Error('Usuário sem permissão cadastrada no sistema.');
    if (!acesso.ativo) throw new Error('Usuário inativo no sistema.');

    return mapSession(acesso, data.user.id, data.user.email || fullEmail);
  }

  async signOut(): Promise<void> {
    await this.supabase.auth.signOut();
  }

  async getCurrentSession(): Promise<AuthSession | null> {
    const { data } = await this.supabase.auth.getSession();
    if (!data.session?.user) return null;

    const acesso = await this.fetchAcesso(data.session.user.id);
    if (!acesso || !acesso.ativo) return null;

    return mapSession(acesso, data.session.user.id, data.session.user.email || '');
  }

  async updatePassword(password: string): Promise<void> {
    const { error } = await this.supabase.auth.updateUser({ password });
    if (error) throw error;
  }

  canAccessCampus(session: AuthSession | null, campusSigla: string): boolean {
    if (!session) return false;
    if (session.podeVerTodosCampus) return true;
    return session.campusSiglas.includes(campusSigla.toUpperCase());
  }

  canAccessModulo(session: AuthSession | null, moduloId: string, campusSigla?: string): boolean {
    const modulosPublicos = ['inventario', 'movimentacao', 'laudo'];
    if (modulosPublicos.includes(moduloId)) return true;
    if (!session) return false;
    if (campusSigla && !this.canAccessCampus(session, campusSigla)) return false;
    if (moduloId === 'base-dados') return session.papel === 'admin';
    if (session.papel === 'admin') return true;
    if (session.papel === 'gestor') {
      return ['verificacao', 'locais', 'usuarios'].includes(moduloId);
    }
    return false;
  }

  canCreateRole(session: AuthSession | null, papel: PapelUsuario): boolean {
    if (!session) return false;
    if (session.papel === 'admin') return true;
    return session.papel === 'gestor' && papel === 'inventariante';
  }

  private async fetchAcesso(userId: string): Promise<UsuarioAcesso | null> {
    const { data, error } = await this.supabase
      .from('usuario_acesso')
      .select('*')
      .eq('user_id', userId)
      .limit(1);

    if (error) throw error;
    return (data?.[0] as UsuarioAcesso | undefined) || null;
  }
}

export const authService = new AuthService();
export default authService;
